import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useMemo } from "react";
import {
  getCategories,
  updateCategoryStatus,
  deleteCategory,
} from "../../services/categoryService";
import {
  Button,
  Flex,
  message,
  Popconfirm,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import RootCategoryResponse from "../../payload/response/RootCategoryResponse";
import { useNavigate } from "react-router-dom";
import { EditFilled, DeleteOutlined } from "@ant-design/icons";
import { onError } from "../../helper/helper";
interface CategoryType extends RootCategoryResponse {
  key: React.ReactNode;
}

const { Title } = Typography;
export default function Categories() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useQuery({
    queryFn: getCategories,
    queryKey: ["categories"],
  });

  const recursive = (data: RootCategoryResponse[], i: number): CategoryType[] => {
    let datas = [] as CategoryType[];
    data?.map?.((item, index) => {
      let object = {
        id: item.id,
        name: item.name,
        isActive: item.isActive,
        key: `${i}-${index}-${item.id}`,
      } as CategoryType;
      if (item?.children?.length > 0) {
        object.children = recursive(item.children, index);
      }
      datas.push(object);
    });
    return datas;
  };

  const extraDataSource = useMemo(() => {
    let datas = [] as CategoryType[];
    data?.list?.map?.((item, index) => {
      let object = {
        id: item.id,
        name: item.name,
        isActive: item.isActive,
        key: index,
      } as CategoryType;
      if (item?.children?.length > 0) {
        object.children = recursive(item.children, index);
      }
      datas.push(object);
    });

    return data ? datas : [];
  }, [data]);
  const { mutate, isPending } = useMutation({
    mutationFn: (id: number) => updateCategoryStatus(id),
    onSuccess: (res) => {
      message.success("Kategori durumu güncellendi");
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
    },
    onError: (error) => {
      message.error("Kategori durumu güncellenirken bir hata oluştu");
    },
  });

  const { mutate: deleteMutate, isPending: isDeleting } = useMutation({
    mutationFn: (categoryId: number) => deleteCategory(categoryId),
    onSuccess: () => {
      message.success("Kategori başarıyla silindi.");
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
    },
    onError: (error: any) => {
      onError(error);
    },
  });

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={2}>Kategoriler</Title>
        <Button
          onClick={() => {
            navigate("/admin/categories/add");
          }}
          type="primary"
        >
          Kategori Ekle
        </Button>
      </Flex>
      <Table<CategoryType>
        loading={isLoading || isFetching}
        columns={[
          {
            title: "Kategori Adı",
            dataIndex: "name",
            key: "name",
          },
          {
            title: "Durum",
            dataIndex: "isActive",
            key: "isActive",
            render: (text, record) => {
              return (
                <Space>
                  {(!record.children ||
                    record?.children?.filter?.((x) => !x.isActive).length >
                    0) && (
                      <Switch
                        loading={isPending}
                        checkedChildren="Aktif"
                        unCheckedChildren="Pasif"
                        onChange={(value) => {
                          mutate(record.id);
                        }}
                        checked={record.isActive}
                      />
                    )}
                </Space>
              );
            },
          },
          {
            title: "İşlem",
            dataIndex: "action",
            key: "action",
            width: "30%",
            render: (text, record) => {
              return (
                <Space>
                  <Button
                    onClick={() => {
                      navigate(`/admin/categories/edit/${record.id}`);
                    }}
                    type="primary"
                  >
                    <EditFilled />
                  </Button>
                  <Popconfirm
                    title="Kategoriyi sil"
                    description="Bu kategoriyi silmek istediğinizden emin misiniz?"
                    onConfirm={() => {
                      if (!isDeleting) {
                        deleteMutate(record.id);
                      }
                    }}
                    okText="Evet"
                    cancelText="Hayır"
                    disabled={isDeleting}
                  >
                    <Button
                      danger
                      loading={isDeleting}
                      disabled={isDeleting}
                    >
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]}
        dataSource={extraDataSource}
      />
    </>
  );
}
